/**
 * A brand kit's COMPOSITION REFERENCES: one artifact document per slide ROLE, generated from the kit's own
 * dress and the site's own words, curated by the user, and later handed to generation as the exemplar.
 *
 * KEYED BY ROLE, NOT BY GEOMETRY, since 2026-09-02. The library used to hold one reference per structure
 * family, which capped it at nine and made "split" mean a layout exercise rather than a slide. A reference
 * now carries both: the ROLE it does (cover, feature list, metrics) and the COMPOSITION it is built on, so
 * the set is as large as the role library and several references can legitimately share a geometry. Records
 * written before this keying have no role; they keep working and are identified by their family.
 *
 * VARIANTS, since 2026-09-05. A role can hold its primary document plus the SAME document on another of the
 * kit's Background colours, stored beside it under `variantKey` = role@ground. Guri, 2026-09-05: "When I say
 * background, it means that it should not pick randomly. It should go and check the color system where we
 * have a background and just design it perfectly. When I say background, it should change the background,
 * not the entire thing." So a variant is not a second composition: it is the ground swapped, text and muted
 * chosen for contrast, the mark chosen for that ground. `referenceKey` still names the role's primary and
 * nothing that finds a document by it has to learn about variants; `variantKey` is what tells them apart.
 *
 * WHERE THEY LIVE. Inside `profile` — the kit's client-owned JSON column — under `references`, the way the
 * kit page already keeps logo variations and colour groups there. Zero backend change, and a kit saved
 * before this file simply has none. NOT in `sources` (a closed provenance type) and NOT in `exampleUrls`
 * (a flat list of storage URLs, and the guard there drops anything that is not one).
 *
 * PAYLOAD MATH, and it is now the reason for the cap. An artifact is ~7k chars at the median against a 20k
 * ceiling, so a full set is 150-450KB riding every profile PATCH and every kit hydrate, and the kits page
 * hydrates every kit the user owns. That is the standing argument for a summary DTO; until there is one,
 * `MAX_REFERENCES` is what keeps the payload finite.
 */
import type { LayoutPattern } from '../style/styleRecord'
import { isLayoutPattern, normalizeHex } from '../style/styleRecord'
import { PATTERN_ORDER } from '../style/styleRender'
import { assignRolesToCompositions, SLIDE_ROLES } from '../style/slideRoles'
import type { MoodboardProfile } from '../types/presentation'
import { resolveKitRoles } from './kitRoles'

export interface KitReference {
  /** The geometry it is built on. Always present: it is what a /deck generation matches against. */
  family: LayoutPattern
  /** The job it does. Absent on records written before the library was keyed by role. */
  role?: string
  /** A complete artifact document. Never above REFERENCE_MAX_CHARS. */
  html: string
  /** false = generated and waiting for a look. Only approved references reach generation. */
  approved: boolean
  /** Epoch ms of this html. */
  at: number
  /** The manifest title, display only. */
  title?: string
  /**
   * `traced` is a 1:1 copy of one of the brand's own template pages, so it is the one kind of document
   * here that is EVIDENCE about the brand rather than something a writer composed on the kit's previous
   * settings. Readers that measure the brand (the type scale off the compositions) prefer it, and a patch
   * or undo carries it forward because a fixed footer does not make the page any less the brand's page.
   * Written 2026-09-05; older traces carry `generated`, and a caller that finds no `traced` record falls
   * back to the roles with a template page assigned.
   */
  origin: 'generated' | 'pinned' | 'traced'
  /** Validator taste flags, plus "came out as <family>" when the manifest disagreed, plus "built without
   *  site text" when only the profile fed it, plus what the geometry gate found. */
  flags?: string[]
  /**
   * STANDING RULES for this role. What the user said while looking at THIS composition ("the figure stays
   * 240px"), which is why they belong to the reference rather than to the kit: the rule is about the metrics
   * slide, and applying it to a cover would be inventing a preference nobody stated.
   *
   * Ride the prompt on fresh generations AND on edits, through `kitRulesPrompt`. They are never projected
   * into `profile.patterns` — see the note on `MoodboardProfile.rules`.
   */
  rules?: string[]
  /**
   * THE GROUND this document sits on: its `--a-bg`, as `#` and six lowercase hex digits. Set on every
   * variant and on a primary that was written on a named ground; absent on records from before variants
   * existed, which sat on whatever the recipe picked.
   */
  ground?: string
  /**
   * A VARIANT: the role's document again on another of the kit's Background colours, keyed BESIDE its
   * primary (`variantKey` = role@ground), never in place of it. Guri, 2026-09-05: "When I say background,
   * it should change the background, not the entire thing." Storing it under its own key is what lets the
   * primary keep answering every caller that finds a role's document by `referenceKey`.
   */
  variant?: true
  /** How many times this slot has been written. The regeneration angle reads it so run 3 does not repeat
   *  run 2's arrangement. */
  run?: number
}

export type ProfileWithReferences = MoodboardProfile & {
  references?: KitReference[]
}

/**
 * A named set of composition references plus the rules that hold across the whole set.
 *
 * Declared HERE rather than in `presentation.ts` because a template holds `KitReference[]` and that type
 * lives in this file; putting the shape in the profile type would make the profile import its own consumer.
 * The profile carries it the way it carries `references`, as an intersection.
 *
 * This week only the rules are read. The set-switching CRUD is the follow-on, and a reader that already
 * copes with an absent field costs nothing now and is what lets that land without touching generation.
 */
export interface KitTemplate {
  id: string
  name: string
  rules?: string[]
  references?: KitReference[]
}

export type ProfileWithTemplates = MoodboardProfile & {
  templates?: KitTemplate[]
  activeTemplateId?: string
}

/** SPINE_MAX in deck15's artifactTheme, restated: an exemplar that cannot ride is not worth storing. */
export const REFERENCE_MAX_CHARS = 20_000

/** The role library plus a little headroom for pinned artifacts. See PAYLOAD MATH above. */
export const MAX_REFERENCES = 28

/** Rules per reference. A prompt block that carries more than this stops being a set of constraints and
 *  starts being a second brief, and the eight-line cap in `kitRulesPrompt` would silently eat the rest. */
export const MAX_REFERENCE_RULES = 12

export const FAMILY_LABEL: Record<LayoutPattern, string> = {
  split: 'Split',
  spectrum: 'Spectrum',
  field: 'Field',
  orbit: 'Orbit',
  ledger: 'Ledger',
  'single-number': 'Single number',
  stack: 'Stack',
  grid: 'Grid',
  'full-bleed': 'Full bleed',
}

/** Roles first, in the library's own narrative order; anything role-less sorts after, by family. */
const ROLE_ORDER = new Map(SLIDE_ROLES.map((r, i) => [r.id, i] as const))
const FAMILY_ORDER = new Map(PATTERN_ORDER.map((p, i) => [p, i] as const))

/**
 * MIGRATION, one line of it. Before the library was keyed by role it held exactly one generated reference
 * per family, written under the role that family had been assigned; this is that assignment, so a stored
 * record with no role is read back onto the tile it was generated for instead of disappearing from the
 * grid. A PINNED artifact is left alone: its family is a fact about the document, not a slot it was
 * written into, and giving it someone else's role would claim it answers a job it never saw.
 */
const LEGACY_ROLE_OF = assignRolesToCompositions(PATTERN_ORDER)

/** What identifies a reference in the set: its role when it has one, else its family. */
export function referenceKey(ref: Pick<KitReference, 'role' | 'family'>): string {
  return ref.role ?? ref.family
}

/**
 * What identifies a DOCUMENT in the set: the role's own key for a primary, role@ground for a variant.
 *
 * `referenceKey` is deliberately not changed to this. Every caller that finds "the cover" by key wants the
 * primary and must keep getting it; only the paths that write, drop or approve one document need to tell a
 * variant from the primary it sits beside, and those are the ones that call this.
 */
export function variantKey(ref: Pick<KitReference, 'role' | 'family' | 'variant' | 'ground'>): string {
  return ref.variant ? `${referenceKey(ref)}@${ref.ground}` : referenceKey(ref)
}

/**
 * A ground is stored EXACTLY as `--a-bg` prints it: `#` and six lowercase hex digits. The reader checks the
 * shape and does not normalise, because `variantKey` is computed from this value on both sides of a save;
 * a `#0B1E5B` written by a caller and read back as `#0b1e5b` would be a variant nobody could find or drop.
 */
const GROUND_HEX = /^#[0-9a-f]{6}$/
function isGround(value: unknown): value is string {
  return typeof value === 'string' && GROUND_HEX.test(value)
}

/** Past every primary a kit could hold: 21 roles plus custom ones plus nine families is nowhere near it. */
const VARIANT_RANK = 1_000

/**
 * Where a reference sits in the set, and therefore WHICH ONE THE CAP DELETES.
 *
 * `order` is the kit's own composition order — the 21 followed by whatever the brand added. Without it
 * every custom composition ranked past all 21 AND past every family, so the `slice(0, MAX_REFERENCES)`
 * below deleted the brand's own compositions first, on READ, with nothing said: the tile generated, the
 * document saved, and the next hydrate dropped it. A caller with no kit in hand falls back to the
 * built-in order, which is what this always did.
 *
 * VARIANTS RANK AFTER EVERY PRIMARY, not merely after their own. The cap is a payload budget and it has to
 * choose: a primary is the only document a role has, a variant is that document again on another ground.
 * So at the cap a variant goes first whichever role it belongs to, and no primary is ever deleted to make
 * room for one. Ranking primaries first is also what keeps every `find(referenceKey(r) === roleId)` caller
 * in the app landing on the primary once a role has variants beside it. Variants of one role keep the
 * order they were added in, because the sort is stable and they share a rank.
 */
function rank(ref: KitReference, order?: Map<string, number>): number {
  const byRole = ref.role !== undefined ? (order ?? ROLE_ORDER).get(ref.role) : undefined
  const primary =
    byRole !== undefined
      ? byRole
      : (order?.size ?? SLIDE_ROLES.length) + (FAMILY_ORDER.get(ref.family) ?? 99)
  return ref.variant ? VARIANT_RANK + primary : primary
}

/** The kit's composition order, for {@link rank}. Cheap: a resolve and a map build, no allocation per ref. */
function orderOf(profile: MoodboardProfile | null | undefined): Map<string, number> {
  return new Map(resolveKitRoles(profile).map((r, i) => [r.id, i] as const))
}

/**
 * How many compositions this kit can still hold documents for.
 *
 * Exported so the thing that ADDS a composition can refuse before the user names it, rather than letting
 * `readReferences` delete the document quietly afterwards. `MAX_REFERENCES` is a payload budget, not a
 * design opinion — see PAYLOAD MATH above.
 */
export function referenceRoomLeft(profile: MoodboardProfile | null | undefined): number {
  return Math.max(0, MAX_REFERENCES - resolveKitRoles(profile).length)
}

/**
 * How many more DOCUMENTS this kit can hold, primaries and variants together.
 *
 * `referenceRoomLeft` counts roles, which is the right question when adding a composition; a variant adds
 * a document to a role that already exists, so the thing that generates one asks this instead and refuses
 * with the room named rather than letting the cap drop the variant on the next read.
 */
export function documentRoomLeft(profile: MoodboardProfile | null | undefined): number {
  return Math.max(0, MAX_REFERENCES - readReferences(profile).length)
}

/**
 * The shape check. A variant is keyed by its ground, so one without a well-formed ground is a record
 * nothing could ever find, drop or approve, and it is refused here; a primary's ground is an annotation
 * and is checked where it is copied through, so a bad one costs the field and not the document.
 */
function isReference(value: unknown): value is KitReference {
  if (!value || typeof value !== 'object') return false
  const r = value as Partial<KitReference>
  return (
    isLayoutPattern(r.family) &&
    typeof r.html === 'string' &&
    r.html.length > 0 &&
    r.html.length <= REFERENCE_MAX_CHARS &&
    typeof r.at === 'number' &&
    Number.isFinite(r.at) &&
    (r.variant !== true || isGround(r.ground))
  )
}

/**
 * The reference's rules, read defensively and BUDGETED against the record's own ceiling.
 *
 * `REFERENCE_MAX_CHARS` is a promise about the whole record, not just its html: an exemplar that cannot ride
 * is not worth storing, and a rule list is text that rides beside it. So the rules take the headroom the html
 * left and no more, oldest first, and the overflow is dropped here rather than at the prompt, where it would
 * have looked like the rule was being obeyed.
 *
 * The html itself is never sacrificed for a rule. A reference with one fewer rule is a working exemplar; a
 * reference with no document is nothing at all.
 */
function readRules(raw: unknown, htmlLength: number): string[] {
  if (!Array.isArray(raw)) return []
  const headroom = REFERENCE_MAX_CHARS - htmlLength
  if (headroom <= 0) return []
  const seen = new Set<string>()
  const kept: string[] = []
  let used = 0
  for (const item of raw as unknown[]) {
    if (typeof item !== 'string') continue
    const text = item.trim()
    if (!text) continue
    const key = text.toLowerCase()
    if (seen.has(key)) continue
    if (used + text.length > headroom) break
    seen.add(key)
    kept.push(text)
    used += text.length
    if (kept.length >= MAX_REFERENCE_RULES) break
  }
  return kept
}

/** The stored set, read defensively: unknown shapes dropped, one per `variantKey` (newest wins), primaries
 *  in library order and then the variants. */
export function readReferences(profile: MoodboardProfile | undefined | null): KitReference[] {
  const raw = (profile as ProfileWithReferences | undefined | null)?.references
  if (!Array.isArray(raw)) return []
  const byKey = new Map<string, KitReference>()
  for (const item of raw) {
    if (!isReference(item)) continue
    const origin = item.origin === 'pinned' ? 'pinned' : item.origin === 'traced' ? 'traced' : 'generated'
    const role =
      typeof item.role === 'string' && item.role
        ? item.role
        : origin === 'generated'
        ? LEGACY_ROLE_OF[item.family]?.id
        : undefined
    const ref: KitReference = {
      family: item.family,
      ...(role ? { role } : {}),
      html: item.html,
      approved: item.approved === true,
      at: item.at,
      origin,
      ...(typeof item.title === 'string' && item.title ? { title: item.title } : {}),
      ...(Array.isArray(item.flags) && item.flags.length
        ? {
            flags: item.flags.filter((f): f is string => typeof f === 'string'),
          }
        : {}),
    }
    // Copied through EXPLICITLY, because this loop rebuilds the record field by field and anything not
    // named here is dropped on the next read. A rule that survives one save and vanishes on reload is the
    // worst version of this feature: the user watched it work.
    const rules = readRules((item as Partial<KitReference>).rules, ref.html.length)
    if (rules.length) ref.rules = rules
    // The variant fields, by name, for the same reason. A variant with a bad ground never reached this
    // line (see `isReference`); a primary with one keeps its document and loses the annotation.
    if (isGround(item.ground)) ref.ground = item.ground
    if (item.variant === true) ref.variant = true
    if (typeof item.run === 'number' && Number.isInteger(item.run) && item.run >= 0) ref.run = item.run
    const key = variantKey(ref)
    const prior = byKey.get(key)
    if (!prior || ref.at >= prior.at) byKey.set(key, ref)
  }
  const order = orderOf(profile)
  return [...byKey.values()].sort((a, b) => rank(a, order) - rank(b, order)).slice(0, MAX_REFERENCES)
}

/** The profile with the set written back; the key is dropped entirely when the set is empty. */
export function withReferences(profile: MoodboardProfile, refs: KitReference[]): MoodboardProfile {
  const { references: _drop, ...rest } = profile as ProfileWithReferences
  void _drop
  // Sorted before the slice, and by THIS kit's order: an unsorted slice drops whatever happens to be
  // last in the array, which after an upsert is the composition just written.
  const order = orderOf(profile)
  const kept = [...refs].sort((a, b) => rank(a, order) - rank(b, order)).slice(0, MAX_REFERENCES)
  const next: ProfileWithReferences = kept.length ? { ...rest, references: kept } : { ...rest }
  return next as unknown as MoodboardProfile
}

/**
 * The set with `next` written in under its `variantKey`: replaced IN PLACE when that document exists, else
 * appended. In place rather than filtered and re-appended so a regenerated variant keeps its position among
 * its role's variants, which is what makes `variantsOf` mean "in the order they were first added".
 */
export function upsertReference(refs: KitReference[], next: KitReference): KitReference[] {
  const key = variantKey(next)
  const at = refs.findIndex((r) => variantKey(r) === key)
  const out = at >= 0 ? refs.map((r, i) => (i === at ? next : r)) : [...refs, next]
  return out.sort((a, b) => rank(a) - rank(b))
}

/** `key` is a `variantKey`: the role's own key drops the primary, role@ground drops that one variant. */
export function dropReference(refs: KitReference[], key: string): KitReference[] {
  return refs.filter((r) => variantKey(r) !== key)
}

/** `key` is a `variantKey`, so approving the primary leaves its variants where they were and vice versa. */
export function setApproved(refs: KitReference[], key: string, approved: boolean): KitReference[] {
  return refs.map((r) => (variantKey(r) === key ? { ...r, approved } : r))
}

/**
 * The approved reference to build a given geometry on, or null.
 *
 * Several roles can share a family now, so this can have more than one candidate; the newest wins, which
 * makes "regenerate the one you like and it takes over" true without a second control. Only approved
 * references ever reach a prompt.
 */
export function approvedReference(
  profile: MoodboardProfile | undefined | null,
  family: LayoutPattern
): KitReference | null {
  const candidates = readReferences(profile).filter((r) => r.family === family && r.approved)
  if (candidates.length === 0) return null
  return candidates.reduce((best, r) => (r.at > best.at ? r : best))
}

/** For the deck binding: the profile without its library. */
export function stripReferences(profile: MoodboardProfile): MoodboardProfile {
  return withReferences(profile, [])
}

/**
 * The approved reference for one ROLE, or null. Preferred over the family lookup when a request named a
 * job: a metrics reference is a better exemplar for "our Q3 numbers" than whatever else happens to share
 * its geometry.
 *
 * With a `ground`, the approved variant on that ground wins and the primary is the fallback; the ground
 * is normalised here because it arrives from a caller (a hex the user typed, a colour group's value), not
 * from storage. Without one the primary is the only answer: an approved variant never stands in for an
 * unapproved primary, because nobody asked for its ground.
 */
export function approvedRoleReference(
  profile: MoodboardProfile | undefined | null,
  roleId: string,
  ground?: string
): KitReference | null {
  const refs = readReferences(profile)
  const wanted = normalizeHex(ground)
  if (wanted) {
    const onGround = refs.find((r) => r.role === roleId && r.variant && r.ground === wanted && r.approved)
    if (onGround) return onGround
  }
  return refs.find((r) => r.role === roleId && !r.variant && r.approved) ?? null
}

/** The variants of one role, in the order they were added. Empty for a role that has only its primary. */
export function variantsOf(profile: MoodboardProfile | undefined | null, roleId: string): KitReference[] {
  return readReferences(profile).filter((r) => r.role === roleId && r.variant === true)
}

/** One rule appended to a reference, deduped case-insensitively and capped. The dedupe is not tidiness:
 *  "keep it that way" is said again on the next slide that gets it wrong, and a prompt that states the same
 *  constraint three times reads as three constraints. Rules belong to the PRIMARY: a variant is the same
 *  document on another ground, so the caller hands the primary in here and `referenceRules` reads it back. */
export function withReferenceRule(ref: KitReference, rule: string): KitReference {
  const text = rule.trim()
  if (!text) return ref
  const existing = ref.rules ?? []
  if (existing.some((r) => r.toLowerCase() === text.toLowerCase())) return ref
  return { ...ref, rules: [...existing, text].slice(-MAX_REFERENCE_RULES) }
}

/**
 * Scoped entries out of `profile.rules[]`, as plain lines.
 *
 * TWO PLACES HOLD A COMPOSITION RULE and both are real. The kit page writes one onto the reference it was
 * said about; the write-back from a deck writes one into `profile.rules` with `scope:'composition'` and the
 * role on the entry, because a deck has the role but not the reference in hand. Generation reads both, so a
 * rule holds whichever surface it was said on. That is the whole promise.
 */
function scopedProfileRules(
  profile: MoodboardProfile | undefined | null,
  scope: 'composition' | 'template',
  roleId?: string
): string[] {
  const raw: unknown = profile?.rules
  if (!Array.isArray(raw)) return []
  const out: string[] = []
  for (const item of raw as unknown[]) {
    if (!item || typeof item === 'string') continue
    const entry = item as { text?: string; scope?: string; role?: string }
    if (entry.scope !== scope) continue
    if (scope === 'composition' && entry.role !== roleId) continue
    const text = typeof entry.text === 'string' ? entry.text.trim() : ''
    if (text) out.push(text)
  }
  return out
}

/** The rules stored against one ROLE, from both places one can live. The reference does not have to be
 *  approved: an unapproved exemplar is a document nobody has blessed yet, but a rule is a sentence the user
 *  said, and it holds either way. Read off the PRIMARY only: a rule is about the role, and a variant carries
 *  none of its own. */
export function referenceRules(
  profile: MoodboardProfile | undefined | null,
  roleId: string | undefined
): string[] {
  if (!roleId) return []
  const onReference = readReferences(profile).find((r) => r.role === roleId && !r.variant)?.rules ?? []
  return [...onReference, ...scopedProfileRules(profile, 'composition', roleId)]
}

/** The active template's rules, or none. Defensive to the last field: templates are the follow-on, and this
 *  reader exists now so that generation starts obeying them the moment they are written rather than needing
 *  a second edit here. */
export function templateRules(profile: MoodboardProfile | undefined | null): string[] {
  const p = profile as ProfileWithTemplates | undefined | null
  const templates = Array.isArray(p?.templates) ? p.templates : []
  const active = templates.length
    ? (templates.find((t) => t && t.id === p?.activeTemplateId) ?? templates[0])
    : undefined
  const onTemplate = Array.isArray(active?.rules) ? active.rules : []
  return [
    ...onTemplate.filter((r): r is string => typeof r === 'string' && r.trim() !== '').map((r) => r.trim()),
    ...scopedProfileRules(profile, 'template'),
  ]
}

/**
 * The role a stored document does, for the paths that only have its manifest.
 *
 * A fresh generation knows the role because it chose it; an EDIT knows only what the document declared,
 * which is a composition family. `LEGACY_ROLE_OF` is the same one-per-family assignment the reference
 * library uses to place a role-less record, so an edit resolves to the tile its rules were written on.
 *
 * Inferred, and the callers say so. It is the honest answer while `DocMeta` carries no role of its own;
 * once generation persists one, the stored value wins here without any caller changing.
 */
export function roleOfDoc(
  meta: { role?: string; composition?: LayoutPattern } | undefined | null
): string | undefined {
  if (!meta) return undefined
  if (typeof meta.role === 'string' && meta.role) return meta.role
  return meta.composition ? LEGACY_ROLE_OF[meta.composition]?.id : undefined
}
